import React, {
  useMemo,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useCallback,
} from 'react';
import { useTranslation } from 'react-i18next';
import useFilterContext from '../../../components/Filter/hooks/useFilterContext';
import { Column } from '@prio365/prio365-react-library/lib/VirtualTable/types/Column';
import FilterResultNoItemsScreen from '../../../components/Filter/FilterResultNoItemsScreen';
import { ProjectId, ProjectRole } from '../../../models/Types';

import { useDispatch, useSelector } from 'react-redux';
import {
  RootReducerState,
  getAllHourlyRates,
  getContactsIsFetching,
  getUserMe,
} from '../../../apps/main/rootReducer';

import FilterContextVirtualTable from '../../../components/Filter/FilterContextVirtualTable';
import {
  AdhancedProjectMemberSearchResultItem,
  ProjectMember,
  ProjectMemberCalculatedData,
  ProjectMemberSearchResultItem,
} from '../../../models/ProjectContacts';
import {
  ButtonProps,
  PillCRA as Pill,
  PrioSpinner,
  ResponsiveContainer,
  Tooltip,
} from '@prio365/prio365-react-library';
import UserAvatar from '../../../components/UserAvatar';
import useCompaniesContext from '../../companies/hooks/useCompaniesContext';
import { HourlyRate } from '../../../models/HourlyRate';
import SvgIcon from '../../../components/SvgIcon';
import { getSvgIconPathByType } from '../../../util/icon';
import { fetchHourlyRates } from '../../projects/actions';
import { useExportProjectMembersToCsv } from '../export';
import { VirtualListItemOnRowProps } from '@prio365/prio365-react-library/lib/VirtualList/components/VirtualListItem';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { openContactsDrawer } from '../actions/drawer';
import { makePrioStyles } from '../../../theme/utils';
import useContactsContext from '../hooks/useContactsProvider';
import { sortContactsHelper } from '../utils';
import { syncContactsMe } from '../actions';
import Flex from '../../../components/Flex';
import { useTheme } from 'react-jss';
import { PrioTheme } from '../../../theme/types';
import { distinctByProperty, generateFilterOptions } from '../../../util';
import { GenericSearchResultItem } from '../../../components/Filter/types';
import useExcelExport from '../../../hooks/useExcelExport';

const useStyles = makePrioStyles((theme) => ({
  tableRow: {
    '&:hover $contactIdCell > div > svg': {
      display: 'block',
    },
  },
  contactIdCell: {
    '& > svg': {
      display: 'none',
    },
  },

  ellipsis: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  label: {
    color: theme.colors.application.typography.muted,
    fontSize: theme.font.fontSize.extraSmall,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  contactDetailsIcon: {
    display: 'none',
    marginLeft: theme.spacing.small,
    cursor: 'pointer',
    height: '14px',
  },
  archivedContact: {
    fontStyle: 'italic',
    opacity: 0.7,
  },
}));

export interface ProjectContactsPageTableRef {
  fetchProjectMember: () => void;
}

export declare type ProjectContactsActions =
  | 'adjustFunction'
  | 'adjustHourlyRate'
  | 'archive'
  | 'archiveByNonProjectMember'
  | 'archiveExternal'
  | 'adjustRoles';

interface ProjectContactsTableProps {
  className?: string;
  projectId: ProjectId;
  tableId: string;
  selectedProjectMembers: ProjectMemberSearchResultItem[];
  onRowClick?: (entry: ProjectMemberSearchResultItem) => void;
  onSelectionChange?: (items: ProjectMemberSearchResultItem[]) => void;
  onDelete?: () => void;
  setProjectContacts?: (value: ProjectMember[]) => void;
  onOpenModal: (action: ProjectContactsActions) => void;
}

export const ProjectContactsPageTable = forwardRef(
  (
    props: ProjectContactsTableProps,
    ref: React.Ref<ProjectContactsPageTableRef>
  ) => {
    //#region ------------------------------ Defaults

    const {
      className,
      projectId,
      tableId,
      onSelectionChange,
      selectedProjectMembers,
      onOpenModal,
      onRowClick,
    } = props;
    const { t } = useTranslation();

    const classes = useStyles();
    const dispatch = useDispatch();
    const theme = useTheme<PrioTheme>();
    //#endregion

    //#region ------------------------------ States / Attributes / Selectors
    const { getContactById } = useContactsContext();
    const { getCompanyById } = useCompaniesContext();
    const hourlyRates = useSelector<RootReducerState, HourlyRate[]>((state) =>
      getAllHourlyRates(state, projectId)
    );

    const exportExcel = useExcelExport();
    const userMe = useSelector(getUserMe);
    const myProjectRoles = useMemo(
      () => (userMe?.prioData.projectRoles[projectId] as ProjectRole[]) || [],
      [userMe, projectId]
    );
    const isGlobalAdmin =
      userMe?.prioData.globalRoles.includes('globalAdmin') || false;
    const isOfficeAdmin =
      userMe?.prioData.officeRoles?.[userMe.myOfficeId]?.includes(
        'officeAdmin'
      ) || false;

    const { data, isLoading, fetchSearch } = useFilterContext<
      ProjectMember,
      ProjectMemberCalculatedData
    >();

    const addHourlyRateColumn =
      isGlobalAdmin ||
      isOfficeAdmin ||
      myProjectRoles.includes('projectAdmin') ||
      myProjectRoles.includes('projectController');

    const isContactsFetching = useSelector(getContactsIsFetching);

    const _projectContacts: AdhancedProjectMemberSearchResultItem[] =
      useMemo(() => {
        return (data?.items ?? [])
          .map((entry) => ({
            entry,
            projectExtensions:
              entry.calculated?.projectExtensionAccessProjectExtensionMaps
                .map(({ projectExtensionType, shouldHaveAccess }) =>
                  shouldHaveAccess ? projectExtensionType : null
                )
                .filter(Boolean),
          }))
          .sort((a, b) =>
            sortContactsHelper(
              getContactById(a.entry.data.contactId),
              getContactById(b.entry.data.contactId)
            )
          );
      }, [data?.items, getContactById]);

    const projectContacts = useMemo(() => _projectContacts, [_projectContacts]);

    const [isCallTriggered, setIsCallTriggered] = React.useState(true);

    const showColumnsFilter = useMemo(() => {
      return _projectContacts?.length > 0;
    }, [_projectContacts]);

    const getContactLabelById = useCallback(
      (id: string): string => {
        const contact = getContactById(id);
        let contactName = '';
        if (contact) {
          contactName = `${contact.firstName} ${contact.lastName}`;
        }
        const company = getCompanyById(contact?.companyId);
        let companyName = '';
        if (company) {
          companyName = `${company.fullName}`;
        }
        return contactName + (companyName ? ` (${companyName})` : '');
      },
      [getContactById, getCompanyById]
    );

    const contactOptions = useMemo(() => {
      return generateFilterOptions<
        GenericSearchResultItem<ProjectMember, ProjectMemberCalculatedData>
      >(data?.items || [], 'data.contactId', getContactLabelById);
    }, [data?.items, getContactLabelById]);

    const jobTitleOptions = useMemo(() => {
      return distinctByProperty(
        generateFilterOptions<
          GenericSearchResultItem<ProjectMember, ProjectMemberCalculatedData>
        >(data?.items || [], 'data.jobTitle', (value) => String(value)),
        'value'
      );
    }, [data?.items]);

    const hourlyRateOptions = useMemo(() => {
      const hourlyRateIdToName = (hourlyRateId: string) => {
        const hourlyRate = hourlyRates.find(
          (hourlyRate) => hourlyRate.hourlyRateId === hourlyRateId
        );
        return hourlyRate ? hourlyRate.name : '';
      };
      return distinctByProperty(
        generateFilterOptions<
          GenericSearchResultItem<ProjectMember, ProjectMemberCalculatedData>
        >(data?.items || [], 'data.hourlyRateId', hourlyRateIdToName),
        'value'
      );
    }, [data?.items, hourlyRates]);

    const projectRolesOptions = useMemo(() => {
      const projectRoles: ProjectRole[] = [
        'projectAdmin',
        'projectController',
        'projectAssistance',
        'projectMember',
      ];
      return projectRoles.map((projectRole) => {
        return {
          value: projectRole,
          label: t(`contacts:projectContactsPage.projectRoles.${projectRole}`),
        };
      });
    }, [t]);

    const projectExtensionOptions = useMemo(() => {
      const projectExtensionTypes = [
        'sharedMailbox',
        'jira',
        'sharepoint',
        'planner',
        'openExperience',
        'proman',
        'amberSearch',
        'prioShare',
      ];
      return projectExtensionTypes.map((projectExtensionType) => {
        return {
          value: projectExtensionType,
          label: t(
            `contacts:projectContactsPage.projectExtensions.${projectExtensionType}`
          ),
        };
      });
    }, [t]);

    const handleOnExportExcelClick = useCallback(
      function <
        T extends
        | AdhancedProjectMemberSearchResultItem
        | ProjectMemberSearchResultItem,
      >(projectMembers: T[] = []) {
        // function that builds all the shared columns

        const isEnhancedType = (
          item:
            | AdhancedProjectMemberSearchResultItem
            | ProjectMemberSearchResultItem
        ): item is AdhancedProjectMemberSearchResultItem => 'entry' in item;

        const exportItems = projectMembers.map((item) => {
          // pull out data/calculated in a uniform way
          const {
            contactId,
            externalProjectContactId,
            jobTitle,
            projectRoles,
            notes,
            hourlyRateId,
          } = isEnhancedType(item) ? item.entry.data : item.data;
          const extensionMaps = isEnhancedType(item)
            ? item.entry.calculated.projectExtensionAccessProjectExtensionMaps
            : item.calculated.projectExtensionAccessProjectExtensionMaps;

          // fetch contact + company just once
          const contact = getContactById(contactId);
          const company = getCompanyById(contact?.companyId ?? '');

          // build your base row
          const row: Record<string, string> = {
            [t('hr:timeAndLeaveManagement.overview.employee')]:
              `${contact?.firstName ?? '-'} ${contact?.lastName ?? ''}`,
            [t('contacts:projectContactsPage.export.company')]:
              company?.fullName ?? '-',
            [t('contacts:projectContactsPage.export.internalExternal')]:
              externalProjectContactId
                ? t('contacts:projectContactsPage.export.external')
                : t('contacts:projectContactsPage.export.internal'),
            [t('contacts:projectContactsPage.columns.jobTitle')]:
              jobTitle ?? '-',
            [t('contacts:projectContactsPage.columns.projectRoles')]:
              projectRoles.length
                ? projectRoles
                  .map((pr) =>
                    t(`contacts:projectContactsPage.projectRoles.${pr}`)
                  )
                  .join(', ')
                : '-',
            [t('contacts:projectContactsPage.columns.addIns')]: extensionMaps
              .filter((m) => m.shouldHaveAccess)
              .map((m) => m.projectExtensionType)
              .join(', '),
            [t('contacts:projectContactsPage.columns.notes')]: notes ?? '-',
          };

          // add the optional hourly rate
          if (addHourlyRateColumn && hourlyRateId) {
            const rateName = hourlyRates.find(
              (hr) => hr.hourlyRateId === hourlyRateId
            )?.name;
            row[t('contacts:projectContactsPage.columns.hourlyRate')] =
              rateName ?? '-';
          }

          return row;
        });

        // c) fire off export
        exportExcel(exportItems, t('contacts:title'));
      },
      [
        exportExcel,
        getContactById,
        t,
        getCompanyById,
        hourlyRates,
        addHourlyRateColumn,
      ]
    );

    //#endregion

    //#region ------------------------------ Methods / Handlers
    const exportToCsv = useExportProjectMembersToCsv();

    const handleClick = useCallback(
      (contactId: string, e) => {
        e.stopPropagation();
        dispatch(
          openContactsDrawer({
            view: 'contactDetail',
            selectedContact: contactId,
          })
        );
      },
      [dispatch]
    );

    const actionBarButtons = useMemo((): ButtonProps[] => {
      const csvExportButton = {
        iconProp: ['fas', 'file-arrow-down'],
        onClick: () => {
          exportToCsv(selectedProjectMembers, hourlyRates);
        },
        children: t('common:actions.csvExportSelection'),
      } as ButtonProps;

      const excelExportButton = {
        children: t('common:actions.excelExportSelection'),
        iconProp: ['fas', 'file-excel'],
        onClick: () => handleOnExportExcelClick(selectedProjectMembers),
      } as ButtonProps;

      const removeProjectMemberButton = {
        iconProp: ['fas', 'trash-alt'],
        onClick: () => {
          onOpenModal('archiveByNonProjectMember');
        },
        children: t('contacts:projectContactsPage.actions.archive'),
      } as ButtonProps;

      const removeExternalButton = {
        iconProp: ['fas', 'trash-alt'],
        onClick: () => {
          onOpenModal('archiveExternal');
        },
        children: t('contacts:projectContactsPage.actions.archiveExternal'),
        disabled: selectedProjectMembers.some(
          (member) => member.data.externalProjectContactId === null
        ),
      } as ButtonProps;

      const adjustFunctionButton = {
        iconProp: ['fas', 'user-cog'],
        onClick: () => {
          onOpenModal('adjustFunction');
        },
        children: t('contacts:projectContactsPage.actions.adjustFunction'),
      } as ButtonProps;

      const adjustHourlyRateButton = {
        iconProp: ['fas', 'euro-sign'],
        onClick: () => {
          onOpenModal('adjustHourlyRate');
        },
        children: t('contacts:projectContactsPage.actions.adjustHourlyRate'),
      } as ButtonProps;

      const adjustRolesButton = {
        iconProp: ['fas', 'lock'],
        onClick: () => {
          onOpenModal('adjustRoles');
        },
        children: t('contacts:projectContactsPage.actions.adjustRoles'),
      } as ButtonProps;

      if ((isGlobalAdmin || isOfficeAdmin) && !myProjectRoles.length) {
        return [removeProjectMemberButton, csvExportButton, excelExportButton];
      }

      if (
        myProjectRoles.includes('projectAdmin') ||
        (myProjectRoles.includes('projectController') &&
          myProjectRoles.includes('projectAssistance'))
      ) {
        return [
          adjustFunctionButton,
          adjustHourlyRateButton,
          adjustRolesButton,
          removeProjectMemberButton,
          excelExportButton,
          csvExportButton,
        ];
      }

      if (myProjectRoles.includes('projectAssistance')) {
        return [
          adjustFunctionButton,
          adjustRolesButton,
          removeProjectMemberButton,
          excelExportButton,
          csvExportButton,
        ];
      }

      if (myProjectRoles.includes('projectController')) {
        return [
          adjustHourlyRateButton,
          removeExternalButton,
          excelExportButton,
          csvExportButton,
        ];
      }

      return [excelExportButton, csvExportButton, removeExternalButton];
    }, [
      myProjectRoles,
      isGlobalAdmin,
      isOfficeAdmin,
      t,
      onOpenModal,
      exportToCsv,
      handleOnExportExcelClick,
      selectedProjectMembers,
      hourlyRates,
    ]);

    const handleOnRowClick: (
      item: AdhancedProjectMemberSearchResultItem
    ) => VirtualListItemOnRowProps = useCallback(
      (item) => {
        return {
          onClick: (e) => {
            onRowClick(item.entry);
          },
        };
      },
      [onRowClick]
    );

    //#endregion

    //#region ------------------------------ Columns
    const columns: Column<AdhancedProjectMemberSearchResultItem>[] =
      useMemo(() => {
        const calcColumnWidth = (width: number) => {
          if (addHourlyRateColumn) return width;
          return width * 1.1;
        };

        const commonColumns: Column<AdhancedProjectMemberSearchResultItem>[] = [
          {
            id: 'contactId',
            accessor: 'entry.data.contactId',
            title: t('contacts:projectContactsPage.columns.name'),
            width: calcColumnWidth(23),
            className: classes.contactIdCell,
            sortingFn: (rowA, rowB) =>
              sortContactsHelper(
                getContactById(rowA.entry.data.contactId),
                getContactById(rowB.entry.data.contactId)
              ),
            filterObject: showColumnsFilter
              ? {
                id: 'contactId',
                options: contactOptions,
              }
              : undefined,
            isDefaultSortingFn: true,
            Cell: ({
              originalData: {
                entry: {
                  data: { contactId, externalProjectContactId, isArchived },
                },
              },
            }) => {
              const contact = getContactById(contactId);
              if (!contact)
                return (
                  <Flex.Row
                    alignItems="center"
                    childrenGap={theme.spacing.small}
                    width="100%"
                  >
                    <PrioSpinner size="small" />
                    <Flex.Item className={classes.label}>
                      {t(
                        'contacts:projectContactsPage.loadingContactInformation'
                      )}
                    </Flex.Item>
                  </Flex.Row>
                );
              return (
                <Flex.Row alignItems="center">
                  <UserAvatar
                    contact={getContactById(contactId)}
                    backgroundColor={theme.colors.base.blue.default}
                    size="small"
                  ></UserAvatar>
                  <div
                    style={{
                      marginLeft: theme.spacing.small,
                      flex: 1,
                      overflow: 'hidden',
                    }}
                  >
                    <div
                      className={`${classes.ellipsis} ${isArchived ? classes.archivedContact : ''}`}
                      style={{
                        fontSize: theme.font.fontSize.small,
                      }}
                    >
                      {getContactById(contactId)?.firstName +
                        ' ' +
                        getContactById(contactId)?.lastName}
                    </div>
                    <div
                      className={`${classes.ellipsis} ${isArchived ? classes.archivedContact : ''}`}
                      style={{
                        fontSize: theme.font.fontSize.tiny,
                      }}
                    >
                      {
                        getCompanyById(getContactById(contactId)?.companyId)
                          ?.fullName
                      }
                    </div>
                  </div>
                  <Tooltip
                    overlay={
                      <div>{t('contacts:projectContactsPage.toContact')}</div>
                    }
                    placement="top"
                  >
                    <FontAwesomeIcon
                      onClick={(e) => handleClick(contactId, e)}
                      icon={['fal', 'address-book']}
                      className={classes.contactDetailsIcon}
                    ></FontAwesomeIcon>
                  </Tooltip>

                  {externalProjectContactId && (
                    <Pill
                      size="small"
                      style={{
                        marginLeft: theme.spacing.small,
                        height: '20px',
                      }}
                      children="Ext."
                    ></Pill>
                  )}
                </Flex.Row>
              );
            },
            alignSelf: true,
          },
          {
            id: 'jobTitle',
            accessor: 'entry.data.jobTitle',
            title: t('contacts:projectContactsPage.columns.jobTitle'),
            width: calcColumnWidth(12),
            sortingFn: (rowA, rowB) =>
              rowA.entry.data.jobTitle?.localeCompare(rowB.entry.data.jobTitle),
            filterObject: showColumnsFilter
              ? {
                id: 'jobTitle',
                options: jobTitleOptions,
              }
              : undefined,
            Cell: ({
              originalData: {
                entry: {
                  data: { jobTitle, isArchived },
                },
              },
              originalData,
            }) => {
              const contact = getContactById(originalData.entry.data.contactId);
              if (!contact) return null;
              return (
                <div
                  className={`${classes.ellipsis} ${isArchived ? classes.archivedContact : ''}`}
                >
                  {jobTitle}
                </div>
              );
            },
            alignSelf: true,
          },
          {
            id: 'projectRoles',
            accessor: 'entry.data.projectRoles',
            title: t('contacts:projectContactsPage.columns.projectRoles'),
            width: 32,
            filterObject: showColumnsFilter
              ? {
                id: 'projectRoles',
                options: projectRolesOptions,
              }
              : undefined,
            Cell: ({
              originalData: {
                entry: {
                  data: { projectRoles },
                },
              },
            }) => (
              <ResponsiveContainer>
                {projectRoles
                  ?.slice()
                  .sort((a, b) => a.localeCompare(b))
                  .map((projectRole, index) => (
                    <Pill
                      key={index}
                      size="small"
                      children={t(
                        `contacts:projectContactsPage.projectRoles.${projectRole}`
                      )}
                      style={{ marginRight: theme.spacing.small }}
                    />
                  ))}
              </ResponsiveContainer>
            ),
            alignSelf: true,
          },
          {
            id: 'projectExtensions',
            accessor: 'projectExtensions',
            title: t('contacts:projectContactsPage.columns.addIns'),
            width: calcColumnWidth(18),
            filterObject: showColumnsFilter
              ? {
                id: 'projectExtensions',
                options: projectExtensionOptions,
              }
              : undefined,
            Cell: ({
              originalData: {
                entry: { calculated },
              },
            }) => (
              <>
                {calculated?.projectExtensionAccessProjectExtensionMaps
                  ?.slice()
                  .sort((a, b) =>
                    b.projectExtensionType?.localeCompare(
                      a.projectExtensionType
                    )
                  )
                  .map((projectExtensionAccessProjectExtensionMap, index) =>
                    projectExtensionAccessProjectExtensionMap.shouldHaveAccess ? (
                      <SvgIcon
                        key={index}
                        width={28}
                        height={28}
                        path={getSvgIconPathByType(
                          projectExtensionAccessProjectExtensionMap.projectExtensionType
                        )}
                      />
                    ) : null
                  )}
              </>
            ),
            alignSelf: true,
          },
          {
            id: 'notes',
            accessor: 'entry.data.notes',
            title: t('contacts:projectContactsPage.columns.notes'),
            width: 5,
            Cell: ({
              originalData: {
                entry: {
                  data: { notes },
                },
              },
            }) => (
              <Flex.Row
                justifyContent="center"
                alignItems="center"
                width="100%"
              >
                {notes !== '' ? (
                  <Tooltip overlay={notes} placement="top">
                    <FontAwesomeIcon
                      icon={['fal', 'notes']}
                      style={{ height: '15px' }}
                    />
                  </Tooltip>
                ) : null}
              </Flex.Row>
            ),
            alignSelf: true,
          },
        ];

        if (addHourlyRateColumn) {
          commonColumns.splice(2, 0, {
            id: 'hourlyRateId',
            accessor: 'entry.data.hourlyRateId',
            title: t('contacts:projectContactsPage.columns.hourlyRate'),
            width: 10,
            filterObject: showColumnsFilter
              ? {
                id: 'hourlyRateId',
                options: hourlyRateOptions,
              }
              : undefined,
            Cell: ({
              originalData: {
                entry: {
                  data: { hourlyRateId, isArchived },
                },
              },
            }) => (
              <div
                className={`${classes.ellipsis} ${isArchived ? classes.archivedContact : ''}`}
              >
                {hourlyRateId &&
                  hourlyRates &&
                  hourlyRates.find(
                    (hourlyRate) => hourlyRate.hourlyRateId === hourlyRateId
                  )?.name}
              </div>
            ),
            alignSelf: true,
          });
        }

        return commonColumns;
      }, [
        getContactById,
        t,
        hourlyRates,
        getCompanyById,
        handleClick,
        classes,
        theme,
        contactOptions,
        showColumnsFilter,
        hourlyRateOptions,
        addHourlyRateColumn,
        jobTitleOptions,
        projectRolesOptions,
        projectExtensionOptions,
      ]);
    //#endregion

    //#region ------------------------------ Refs
    useImperativeHandle(ref, () => ({
      fetchProjectMember: () => {
        fetchSearch();
      },
    }));
    //#endregion

    //#region ------------------------------ Effects
    useEffect(() => {
      if (_projectContacts) {
        const contactIds = _projectContacts.map(
          (item) => item.entry.data.contactId
        );
        const missingContactIds = contactIds.filter(
          (contactId) => !getContactById(contactId)
        );
        if (
          missingContactIds.length > 0 &&
          !isContactsFetching &&
          isCallTriggered
        ) {
          // only trigger once to prevent too many calls when failing
          setIsCallTriggered(false);
          dispatch(syncContactsMe());
        }
      }
    }, [
      _projectContacts,
      getContactById,
      dispatch,
      isContactsFetching,
      isCallTriggered,
    ]);

    useEffect(() => {
      if (
        isGlobalAdmin ||
        isOfficeAdmin ||
        myProjectRoles.includes('projectAdmin') ||
        myProjectRoles.includes('projectController')
      ) {
        dispatch(fetchHourlyRates(projectId));
      }
    }, [dispatch, projectId, myProjectRoles, isGlobalAdmin, isOfficeAdmin]);
    //#endregion

    return (
      <FilterContextVirtualTable<AdhancedProjectMemberSearchResultItem>
        id={tableId}
        className={className}
        data={projectContacts}
        columns={columns}
        onSelectionChange={(data) =>
          onSelectionChange(data.map(({ entry }) => entry))
        }
        onCheckEquality={(a, b) =>
          a.entry.data.contactId === b.entry.data.contactId
        }
        resizable="relative"
        noItemsScreen={<FilterResultNoItemsScreen />}
        loading={
          isLoading && {
            type: 'noItems',
          }
        }
        rowsAreSelectable
        onRow={handleOnRowClick}
        classNameTableRow={classes.tableRow}
        actionBarButtons={actionBarButtons}
        actionBarDefaultButtons={[
          ...(data?.totalItems > 0
            ? ([
              {
                children: t('common:actions.csvExport'),
                iconProp: 'file-arrow-down',
                onClick: () => {
                  exportToCsv(projectContacts, hourlyRates);
                },
              },
              {
                children: t('common:actions.excelExport'),
                iconProp: 'file-excel',
                onClick: () => handleOnExportExcelClick(projectContacts),
              },
            ] as Array<ButtonProps>)
            : []),
        ]}
      />
    );
  }
);

export default ProjectContactsPageTable;
